import type { JSX } from "react";
import { useContext, useState } from "react";
import UiCard from "../../../components/card/Card";
import { ParticipantContextProps } from "../../../models/contexts/participant-context.model";
import { ParticipantContext } from "../../../contexts/Participant.context";
import ParticipantDetailsGeneralInfo from "./GeneralInformation";
import ParticipantDetailsKannactInfo from "./KannactInformation";
import ParticipantDetailsSubscriptions from "./SubscriptionsPreferences";
import ParticipantDetailsContact from "./ContactPreferences";
import { useAddressStates } from "../../../hooks/useAddressStates";
import { eParticipantStatus } from "../../../models/entities/participant.model";
import UiButton from "../../../components/button/Button";
import { PageProps } from "../../../models/routing.model";
import ModalProfileEvents, { ModalProfileEventsProps } from "./ModalProfileEvents";
import { useInstitutionsDetailed } from "../../../hooks/useInstitutionsDetailed";
import ParticipantDetailsPhysicians from "./Physicians";
import ParticipantDetailsCaregivers from "./Caregivers";
import ParticipantDetailsEhrMrn from "./EhrMrn";
import MarketingCampaigns from "./MarketingCampaigns";

const ParticipantDetails = (props: PageProps) => {
	const participantContext = useContext<ParticipantContextProps>(ParticipantContext);

	const [modalProps, setModalProps] = useState<
		Omit<ModalProfileEventsProps, "onClose" | "onComplete" | "participantId">
	>({
		isVisible: false,
		mode: "OPT_OUT",
	});

	// Kannact information
	const institutions = useInstitutionsDetailed();

	// Contact preferences
	const addressStatesQuery = useAddressStates();

	return (
		<UiCard
			title="PARTICIPANT.DETAILS.TAB_TITLE"
			className="participant-details-container"
			titleUnderline={false}
			customHeader={
				<>
					{(() => {
						const status = participantContext.participant!.status;
						const buttons: JSX.Element[] = [];

						// Prospective: Mark as do not contact, Snooze, Mark as unreachable, Finalize enrollment
						if (status === eParticipantStatus.PROSPECTIVE) {
							buttons.push(
								<UiButton
									key="opt-out"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.OPT_OUT"
									className="p-button-rounded p-button-sm p-button-outlined"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "OPT_OUT",
										})
									}
								/>,
								<UiButton
									key="snooze"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.SNOOZE"
									className="p-button-rounded p-button-sm p-button-outlined ms-2"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "SNOOZE",
										})
									}
								/>,
								<UiButton
									key="unreachable"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.MARK_UNREACHABLE"
									className="p-button-rounded p-button-sm p-button-outlined ms-2"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "UNREACHABLE",
										})
									}
								/>
							);
							if (!participantContext.participant!.enrollmentDate) {
								buttons.push(
									<UiButton
										key="enrolled"
										label="PARTICIPANT.DETAILS.HEADER_ACTIONS.ENROLLED"
										className="p-button-rounded p-button-sm ms-2"
										onClick={() =>
											setModalProps({
												isVisible: true,
												mode: "ENROLLED",
											})
										}
									/>
								);
							}
						}
						// Active: Mark as do not contact, Snooze, Mark as unreachable
						else if (status === eParticipantStatus.ACTIVE) {
							buttons.push(
								<UiButton
									key="opt-out"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.OPT_OUT"
									className="p-button-rounded p-button-sm p-button-outlined"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "OPT_OUT",
										})
									}
								/>,
								<UiButton
									key="snooze"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.SNOOZE"
									className="p-button-rounded p-button-sm p-button-outlined ms-2"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "SNOOZE",
										})
									}
								/>,
								<UiButton
									key="unreachable"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.MARK_UNREACHABLE"
									className="p-button-rounded p-button-sm p-button-outlined ms-2"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "UNREACHABLE",
										})
									}
								/>
							);
						}
						// Inactive: Mark as do not contact, Snooze (mark as unreachable is not offered — the healer immediately reverts it)
						else if (status === eParticipantStatus.INACTIVE) {
							buttons.push(
								<UiButton
									key="opt-out"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.OPT_OUT"
									className="p-button-rounded p-button-sm p-button-outlined"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "OPT_OUT",
										})
									}
								/>,
								<UiButton
									key="snooze"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.SNOOZE"
									className="p-button-rounded p-button-sm p-button-outlined ms-2"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "SNOOZE",
										})
									}
								/>
							);
						}
						// Unreachable: Mark as Reachable, Mark as do not contact
						else if (status === eParticipantStatus.UNREACHABLE) {
							buttons.push(
								<UiButton
									key="reachable"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.MARK_REACHABLE"
									className="p-button-rounded p-button-sm ms-2"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "REACHABLE",
										})
									}
								/>,
								<UiButton
									key="opt-out"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.OPT_OUT"
									className="p-button-rounded p-button-sm p-button-outlined ms-2"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "OPT_OUT",
										})
									}
								/>
							);
						}
						// Snoozed: Unsnooze, Mark as do not contact
						else if (status === eParticipantStatus.SNOOZED) {
							buttons.push(
								<UiButton
									key="unsnooze"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.UNSNOOZE"
									className="p-button-rounded p-button-sm ms-2"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "UNSNOOZE",
										})
									}
								/>,
								<UiButton
									key="opt-out"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.OPT_OUT"
									className="p-button-rounded p-button-sm p-button-outlined ms-2"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "OPT_OUT",
										})
									}
								/>
							);
						}
						// DNC: Mark as contactable
						else if (status === eParticipantStatus.DNC) {
							buttons.push(
								<UiButton
									key="opt-in"
									label="PARTICIPANT.DETAILS.HEADER_ACTIONS.OPT_IN"
									className="p-button-rounded p-button-sm p-button-outlined"
									onClick={() =>
										setModalProps({
											isVisible: true,
											mode: "OPT_IN",
										})
									}
								/>
							);
						}

						return buttons;
					})()}
				</>
			}
		>
			<div className="row">
				<div className="col-12 col-lg-6">
					{/* General information */}
					<ParticipantDetailsGeneralInfo
						participant={participantContext.participant!}
						onUpdate={participantContext.onRefresh}
					/>

					{/* Contact preferences */}
					<ParticipantDetailsContact
						participant={participantContext.participant!}
						addressStatesQuery={addressStatesQuery}
						onUpdate={participantContext.onRefresh}
					/>

					{/* Physicians information */}
					<ParticipantDetailsPhysicians
						participant={participantContext.participant!}
						participantContext={participantContext}
					/>

					{/* Caregivers information */}
					<ParticipantDetailsCaregivers
						participant={participantContext.participant!}
						participantContext={participantContext}
					/>

					{/* EHR Medical record number */}
					<ParticipantDetailsEhrMrn
						participant={participantContext.participant!}
						participantContext={participantContext}
					/>
				</div>

				<div className="col-12 col-lg-6">
					{/* Kannact information */}
					<ParticipantDetailsKannactInfo
						participant={participantContext.participant!}
						institutions={institutions.data || []}
						onUpdate={participantContext.onRefresh}
					/>

					{/* Subscriptions preferences */}
					<ParticipantDetailsSubscriptions
						participant={participantContext.participant!}
						onUpdate={participantContext.onRefresh}
					/>

					<MarketingCampaigns participant={participantContext.participant!} />
				</div>
			</div>

			{modalProps.isVisible && (
				<ModalProfileEvents
					isVisible={modalProps.isVisible}
					participantId={participantContext.participant!.id}
					mode={modalProps.mode}
					onClose={() => setModalProps({ ...modalProps, isVisible: false })}
					onComplete={() => {
						setModalProps({ ...modalProps, isVisible: false });
						participantContext.onRefresh();
					}}
				/>
			)}
		</UiCard>
	);
};

export default ParticipantDetails;
